import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, FolderSearchIcon, LayersIcon, ShieldCheckIcon, TimerIcon, UsersIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { pageSamples as samples } from './samples';

// RELIGIA GOTOWA™ copy (Polish). Single source: religia-gotowa/PRODUCTO.md.
// Product mockup (WebP 2048², transparent) on Higgsfield CDN — see religia-gotowa/media/MOCKUP.md.
export const PRODUCT_IMAGE = 'https://d2ol7oe51mr4n9.cloudfront.net/user_37MCUZnyYhEzEhcHxirwYMezT12/69d0aaf1-776c-463a-a997-4361137e6730.webp';
export const DISCLAIMER = 'Materiały pomocnicze dla katechetów. Nie jest to publikacja Konferencji Episkopatu Polski ani MEN. Zasady przygotowania do sakramentów ustala twoja diecezja i parafia.';
export const heroGuarantees = ['Natychmiastowy dostęp do pobrania', 'Jednorazowa płatność', 'Bez subskrypcji', '7 dni gwarancji satysfakcji'];
export const stats = [{
  value: '6',
  label: 'Cyfrowych tomów dla katechety'
}, {
  value: '240',
  label: 'Stron gotowych materiałów'
}, {
  value: '1–8',
  label: 'Klasy szkoły podstawowej'
}, {
  value: '3',
  label: 'Poziomy w każdej karcie pracy'
}, {
  value: '5',
  label: 'Bonusów w zestawie'
}];
export const differenceStats = [{
  value: 'Klasy 1–8',
  label: 'Jeden system dla wszystkich klas szkoły podstawowej'
}, {
  value: 'Rok 2026/2027',
  label: 'Plan roczny z datami roku liturgicznego'
}, {
  value: 'Jedna płatność',
  label: 'Cały rok – a nie pakiet na jeden miesiąc'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Osiem klas, jedna lekcja w tygodniu',
  text: 'Co tydzień przygotowujesz tematy dla wielu różnych klas – a na każdy masz tylko 45 minut.'
}, {
  title: 'Uczniowie na różnych poziomach',
  text: 'Jedni potrzebują wsparcia, inni kończą kartę pracy w pięć minut. Do tego grupy międzyoddziałowe.'
}, {
  title: 'Materiały kupowane co miesiąc',
  text: 'Pakiet na jeden miesiąc kończy się szybciej, niż myślisz – i znowu szukasz, co dalej.'
}, {
  title: 'Jasełka, akademie i rekolekcje',
  text: 'Scenariusze, role i próby – zwykle przygotowywane po godzinach.'
}, {
  title: 'Pierwsza Komunia w klasie 3',
  text: 'Chcesz dobrze przygotować uczniów razem z parafią i rodzicami, a czasu jest mało.'
}, {
  title: 'Sprawdziany i oceny od zera',
  text: 'Kartkówki, sprawdziany, klucze odpowiedzi i kryteria oceniania – wszystko piszesz sam.'
}];
export const afterItems = ['Plan roczny 2026/2027 dla klas 1–8 z datami roku liturgicznego', '12 lekcji minuta po minucie (45 min)', 'Karty pracy w 3 poziomach: wsparcie, podstawa, wyzwanie', '16 historii biblijnych krok po kroku', '15 tematów roku liturgicznego: od różańca do Bożego Ciała', 'Sakramenty i Msza Święta – lekcje, sprawdziany i klucz', 'Modlitwa, święci i wartości chrześcijańskie', '8 sprawdzianów, kartkówki i 100 gotowych komentarzy', 'Wszystko w PDF do druku'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Wiele klas, jedna godzina',
  text: 'Czy co tydzień przygotowujesz lekcje dla kilku różnych klas – a w każdej masz tylko jedną lekcję religii?'
}, {
  icon: LayersIcon,
  title: 'Różne poziomy w jednej klasie',
  text: 'Czy jedni uczniowie potrzebują wsparcia, a inni się nudzą, bo skończyli kartę pracy w pięć minut?'
}, {
  icon: FolderSearchIcon,
  title: 'Materiały co miesiąc od nowa',
  text: 'Czy co miesiąc szukasz albo dokupujesz nowe pomysły na lekcje?'
}, {
  icon: CalendarCheckIcon,
  title: 'Adwent, Wielki Post, Wielkanoc',
  text: 'Czy przed każdym okresem liturgicznym przygotowujesz lekcje, roraty i Drogę Krzyżową od zera?'
}, {
  icon: UsersIcon,
  title: 'Jasełka, akademie i rodzice',
  text: 'Czy scenariusze jasełek, akademie i wiadomości do rodziców piszesz po godzinach?'
}, {
  icon: ClipboardCheckIcon,
  title: 'Sprawdziany i oceny',
  text: 'Czy sprawdziany, klucze odpowiedzi i kryteria oceniania przygotowujesz samodzielnie?'
}];

// Spine colours mirror the box (navy, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Tom 01',
  level: 'Planowanie',
  title: 'Plan roczny 2026/2027 i 12 lekcji minuta po minucie',
  tagline: 'Wiesz, co robić na każdej lekcji – od września do czerwca.',
  intro: 'Kalendarz roku szkolnego i liturgicznego 2026/2027, plan roczny dla każdej klasy od 1 do 8 (około 30 tematów, święta i wydarzenia) oraz 12 lekcji rozpisanych minuta po minucie.',
  items: ['Kalendarz roku szkolnego i liturgicznego 2026/2027', 'Plan roczny dla klas 1, 2, 3, 4, 5, 6, 7 i 8', 'Struktura lekcji 45 minut', '3 poziomy na jednej lekcji', '12 lekcji: strona katechety + karta pracy', 'Lista kontrolna katechety na cały rok'],
  outro: 'Otwierasz, wybierasz lekcję i wchodzisz do klasy z planem w 5 minut.',
  note: 'Jeśli w twojej szkole religia ma więcej lekcji w tygodniu, rozwiń każdy temat na dwie lekcje.',
  spine: 'bg-blue-900 text-white',
  rule: 'border-white/30'
}, {
  number: 'Tom 02',
  level: 'Pismo Święte',
  title: 'Pismo Święte na lekcji: 16 historii biblijnych krok po kroku',
  tagline: 'Historie biblijne opowiedziane na miarę uczniów.',
  intro: '8 historii ze Starego i 8 z Nowego Testamentu – każda opowiedziana prostymi słowami na stronie katechety, z kartą pracy w 3 poziomach i kluczem.',
  items: ['Biblia – biblioteka 73 ksiąg i jak czytać sigla', 'Oś historii zbawienia', 'Od stworzenia świata do Daniela w jaskini lwów', 'Od Zwiastowania do Zacheusza', 'Karta pracy w 3 poziomach + klucz', 'Quiz biblijny (20 pytań) i gry biblijne'],
  outro: 'Słowo Boże na każdej lekcji – gotowe dla każdego poziomu.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Tom 03',
  level: 'Rok liturgiczny',
  title: 'Rok liturgiczny i święta 2026/2027',
  tagline: 'Każde święto przygotowane z wyprzedzeniem.',
  intro: 'Koło roku liturgicznego, kalendarz dat 2026/2027, kolory liturgiczne i 15 tematów – każdy z lekcją i kartą pracy.',
  items: ['Różaniec, Wszystkich Świętych i Zaduszki', 'Adwent, roraty, wieniec adwentowy i św. Mikołaj', 'Boże Narodzenie: Wigilia i polskie tradycje', 'Środa Popielcowa, Wielki Post i Droga Krzyżowa dla dzieci', 'Triduum Paschalne, Wielkanoc i święconka', 'Kalendarz adwentowy na 24 dni i karta wielkopostnych postanowień'],
  outro: 'Od różańca w październiku do Bożego Ciała.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Tom 04',
  level: 'Sakramenty',
  title: 'Sakramenty, Msza Święta i życie Kościoła',
  tagline: 'Siedem sakramentów zrozumiałych dla uczniów.',
  intro: '7 sakramentów, wnętrze kościoła, przedmioty i szaty liturgiczne, części Mszy Świętej i postawy oraz 11 tematów z kartą pracy.',
  items: ['Msza Święta krok po kroku', 'Chrzest, Eucharystia i bierzmowanie', 'Sakrament pokuty – 5 warunków i rachunek sumienia dla dzieci', 'Pierwsza spowiedź i Komunia w kl. 3: szkoła – parafia – rodzina', 'Sprawdziany dla kl. 3, 4–6 i 7–8 z kluczem', 'Modlitwy przed i po Komunii'],
  outro: 'Dobre przygotowanie – razem z parafią i rodzicami.',
  note: 'Zasady przygotowania do sakramentów ustala twoja diecezja i parafia.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Tom 05',
  level: 'Modlitwa i święci',
  title: 'Modlitwa, święci i wartości chrześcijańskie',
  tagline: 'Modlitwa i dobre wzory na co dzień.',
  intro: 'Modlitwy codzienne z pełnymi tekstami, 20 tajemnic różańca, katechizm w pigułce, błogosławieństwa i 12 lekcji o modlitwie, świętych i wartościach.',
  items: ['Modlitwy codzienne i różaniec', 'Katechizm w pigułce i błogosławieństwa (Mt 5,3-12)', 'Św. Jan Paweł II, św. Faustyna, św. Maksymilian Kolbe', 'Św. Carlo Acutis i bł. Rodzina Ulmów', 'Przebaczenie, prawda, dobroć i pomoc', 'Kalendarz świętych patronów i karta „Mój patron”'],
  outro: 'Święci, którzy inspirują uczniów.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Tom 06',
  level: 'Ocenianie',
  title: 'Ocenianie: sprawdziany, kartkówki i kryteria',
  tagline: 'Oceniasz uczciwie – bez pisania wszystkiego od zera.',
  intro: 'Zasady oceniania na religii (skala 1–6), wymagania edukacyjne na oceny, 8 sprawdzianów z kluczem i punktacją, kartkówki, uniwersalne karty pracy i 100 gotowych komentarzy.',
  items: ['Wymagania edukacyjne: kl. 1–3, 4–6 i 7–8', 'Kryteria: zeszyt, aktywność, praca w grupie', '8 sprawdzianów (kl. 1–8) z kluczem i punktacją', 'Kartkówki 10-minutowe z kluczem', 'Przeliczanie punktów na oceny', '100 gotowych komentarzy i arkusz ocen'],
  outro: 'Oceniamy wiedzę, umiejętności i pracę ucznia – nigdy jego wiarę.',
  note: DISCLAIMER,
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 cyfrowych tomów dla katechety',
  text: 'System sześć w jednym do prowadzenia lekcji religii w klasach 1–8 – przez cały rok szkolny 2026/2027, od września do czerwca.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Gotowe lekcje',
  text: 'Plan roczny dla każdej klasy i 12 lekcji minuta po minucie – nie zaczynasz od pustej kartki.'
}, {
  icon: LayersIcon,
  title: '3 poziomy w każdej karcie pracy',
  text: 'Wsparcie, podstawa, wyzwanie – cała klasa pracuje nad tym samym tematem i nikt się nie nudzi.'
}, {
  icon: CalendarCheckIcon,
  title: 'Cały rok liturgiczny 2026/2027',
  text: 'Od Wszystkich Świętych i Adwentu, przez Wielki Post i Wielkanoc, po Boże Ciało – z datami na ten rok.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Sprawdziany z kluczem',
  text: '8 sprawdzianów dla klas 1–8, kartkówki, kryteria oceniania i 100 gotowych komentarzy.'
}, {
  icon: TimerIcon,
  title: 'Każda minuta się liczy',
  text: 'Masz tylko jedną lekcję w tygodniu – dlatego każda lekcja jest rozpisana na 45 minut.'
}, {
  icon: ShieldCheckIcon,
  title: 'Pasuje do każdego podręcznika',
  text: 'Oparte na tematach katechezy szkolnej, uporządkowane według tematów i roku liturgicznego.'
}];

// Real Tom 1 sample pages live in data/samples.ts (empty = section hidden).
export const pageSamples = samples;
export const pageTypes = ['Plan roczny', 'Lekcje minuta po minucie', 'Karty pracy w 3 poziomach', 'Klucz dla katechety'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
  value: string;
}> = [{
  title: 'Jasełka i akademie – gotowe scenariusze',
  text: 'Jasełka na 20–25 ról z tradycyjnymi kolędami, akademia na Dzień Papieski, Droga Krzyżowa dla dzieci, nabożeństwo majowe i zakończenie roku.',
  items: ['Jasełka na 20–25 ról', 'Akademia na Dzień Papieski', 'Droga Krzyżowa dla dzieci', 'Nabożeństwo majowe i zakończenie roku'],
  benefit: 'Uroczystości szkolne bez nerwów w ostatniej chwili.',
  value: '49 zł'
}, {
  title: 'Modlitwa i rozgrzewka – 5 minut na początek lekcji',
  text: '40 kart: modlitwa, pytanie na start, zagadka biblijna i gra wyciszająca.',
  items: ['40 kart do druku', 'Modlitwa i pytanie na start', 'Zagadka biblijna i gra wyciszająca'],
  benefit: 'Spokojny i dobry początek każdej lekcji.',
  value: '29 zł'
}, {
  title: 'Komunikacja z rodzicami',
  text: '30 gotowych wiadomości (Librus, e-dziennik, e-mail): start roku i deklaracja, rekolekcje, I Komunia, roraty, wyjście do kościoła, oceny i zebranie.',
  items: ['30 gotowych wiadomości', 'Start roku i rekolekcje', 'I Komunia i roraty', 'Oceny i zebranie'],
  benefit: 'Mniej czasu na pisanie do rodziców.',
  value: '29 zł'
}, {
  title: 'Kit na zastępstwa',
  text: '12 lekcji bez przygotowania dla klas 1–3, 4–6 i 7–8 – każda z kartą pracy.',
  items: ['12 gotowych lekcji', 'Klasy 1–3, 4–6 i 7–8', 'Z kartą pracy'],
  benefit: 'Spokój, gdy nie możesz poprowadzić lekcji osobiście.',
  value: '39 zł'
}, {
  title: 'Plakaty i kolorowanki do sali',
  text: '20 plakatów (rok liturgiczny, kolory liturgiczne, Dekalog, 7 sakramentów, części Mszy, modlitwy, różaniec) i 20 kolorowanek.',
  items: ['20 plakatów do druku', '20 kolorowanek', 'Dekalog, sakramenty i części Mszy'],
  benefit: 'Sala, która uczy każdego dnia.',
  value: '39 zł'
}];
export const BONUS_TOTAL = '185 zł';
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Otwórz RELIGIA GOTOWA™',
  text: 'Pobierz pakiet i otwórz potrzebny tom.'
}, {
  title: 'Wybierz lekcję',
  text: 'Wybierz temat dla swojej klasy i kartę pracy na odpowiednim poziomie.'
}, {
  title: 'Wydrukuj',
  text: 'Wydrukuj kartę pracy albo miej plan lekcji w telefonie lub tablecie.'
}, {
  title: 'Wejdź do klasy',
  text: 'Prowadź lekcję z gotowym planem minuta po minucie.'
}];
export const audienceItems = ['Uczysz religii w szkole podstawowej (klasy 1–8)', 'Jesteś katechetą świeckim, siostrą zakonną lub księdzem', 'Prowadzisz też katechezę w parafii', 'Masz kilka klas i w każdej jedną lekcję religii w tygodniu', 'Masz w klasie uczniów na różnych poziomach', 'Przygotowujesz klasę 3 do Pierwszej Komunii', 'Chcesz mieć plan na cały rok, a nie pakiet na jeden miesiąc', 'Chcesz mieć pod ręką sprawdziany, jasełka i wiadomości do rodziców'];
export const timeValueTasks = ['Przygotowanie tematów dla kilku klas co tydzień', 'Szukanie i dokupowanie materiałów co miesiąc', 'Dopasowanie kart pracy do różnych poziomów', 'Pisanie sprawdzianów i kluczy odpowiedzi', 'Scenariusze jasełek i akademii'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 cyfrowych tomów (240 stron)',
  detail: 'Plan roczny, Pismo Święte, rok liturgiczny, sakramenty, modlitwa i święci, ocenianie'
}, {
  label: `5 bonusów o wartości ${BONUS_TOTAL} – bez dodatkowych kosztów`,
  detail: 'Jasełka i akademie, modlitwa na start, wiadomości do rodziców, kit na zastępstwa, plakaty i kolorowanki'
}, {
  label: 'Dla klas 1–8',
  detail: 'Lekcje 45 min minuta po minucie, karty pracy w 3 poziomach'
}, {
  label: 'Cały rok szkolny 2026/2027',
  detail: 'Jedna płatność – cały rok, nie jeden miesiąc'
}];
export const offerIncludes = ['Natychmiastowy dostęp do pobrania', 'Pliki PDF do zapisania', 'Materiały do druku', 'Nie wysyłamy produktu fizycznego'];
export const PRICE = '99 zł';
export const OLD_PRICE = '199 zł';

// No testimonials on this page.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
  topic: string;
}> = [];
export const faqs = [{
  q: 'Jak otrzymam materiały?',
  a: 'RELIGIA GOTOWA™ jest produktem cyfrowym. Po zakupie otrzymujesz dostęp do plików PDF, które możesz pobrać i zapisać. Możesz korzystać z nich na komputerze, tablecie lub telefonie i drukować to, czego potrzebujesz.'
}, {
  q: 'Czy to oficjalny program KEP albo MEN?',
  a: 'Nie. RELIGIA GOTOWA™ to niezależne materiały pomocnicze dla katechetów, oparte na tematach katechezy szkolnej. Nie jest to publikacja Konferencji Episkopatu Polski ani MEN.'
}, {
  q: 'Czy pasuje do mojego podręcznika?',
  a: 'Tak. Materiały są uporządkowane według tematów i roku liturgicznego, więc wykorzystasz je z każdym podręcznikiem do religii.'
}, {
  q: 'Mam jedną lekcję religii w tygodniu. Czy to dla mnie?',
  a: 'Tak. Plan roczny ma dla każdej klasy około 30 tematów – jeden na tydzień, a każda lekcja jest rozpisana na 45 minut. Jeśli w twojej szkole religia ma więcej lekcji w tygodniu, rozwiń każdy temat na dwie lekcje.'
}, {
  q: 'To materiały na cały rok czy na jeden miesiąc?',
  a: 'Na cały rok szkolny 2026/2027 – od września do czerwca, dla klas 1–8, z datami roku liturgicznego. Płacisz raz i nie musisz co miesiąc dokupować kolejnych pakietów.'
}, {
  q: 'Co z uczniami na różnych poziomach?',
  a: 'Każda karta pracy ma 3 poziomy: A – wsparcie, B – podstawa, C – wyzwanie. Cała klasa pracuje nad tym samym tematem, a każdy uczeń na swoim poziomie – także uczniowie ze specjalnymi potrzebami edukacyjnymi.'
}, {
  q: 'Czy jest coś o Pierwszej Komunii?',
  a: 'Tak. Tom 4 zawiera lekcje o sakramentach, rachunek sumienia dla dzieci, stronę o pierwszej spowiedzi i Komunii w klasie 3 (szkoła – parafia – rodzina) oraz modlitwy przed i po Komunii. Zasady przygotowania do sakramentów ustala twoja diecezja i parafia.'
}, {
  q: 'Czy na religii ocenia się wiarę ucznia?',
  a: 'Nie. W materiałach oceniamy wiedzę, umiejętności, zeszyt, pracę i aktywność – nigdy wiarę ani praktyki religijne ucznia.'
}, {
  q: 'Czy naprawdę przygotuję lekcję w 5 minut?',
  a: 'RELIGIA GOTOWA™ powstała po to, by przyspieszyć przygotowanie lekcji. Otwierasz tom, wybierasz gotową lekcję i kartę pracy i zapoznajesz się z nimi w około pięć minut. Czas może się różnić w zależności od klasy i potrzebnych dostosowań.'
}, {
  q: 'Czy mogę wydrukować materiały?',
  a: 'Tak. Wszystko jest w PDF do druku: karty pracy, sprawdziany, kartkówki, plakaty i kolorowanki. Nie musisz drukować wszystkiego – z planów lekcji możesz korzystać na telefonie lub tablecie.'
}, {
  q: 'Czy pięć bonusów jest w cenie?',
  a: `Tak. Wszystkich pięć bonusów (wartość ${BONUS_TOTAL}) jest w pakiecie bez dodatkowych kosztów – całość za ${PRICE}.`
}, {
  q: 'Czy to subskrypcja?',
  a: 'Nie. Płacisz jednorazowo i otrzymujesz dostęp do materiałów cyfrowych. Bez żadnych opłat cyklicznych.'
}, {
  q: 'Czy jest gwarancja?',
  a: 'Tak. RELIGIA GOTOWA™ objęta jest 7-dniową gwarancją satysfakcji. Jeśli materiały nie spełnią twoich oczekiwań, możesz skontaktować się z nami i poprosić o zwrot pieniędzy w okresie gwarancji, zgodnie z jej warunkami.'
}];
export const footerLinks = ['Regulamin', 'Polityka prywatności', 'Polityka zwrotów', 'Kontakt'];
